import { observable } from 'mobx';
import { copyPropertiesExcludeNullsAndGetters } from '../../utils/ObjectUtils';

export interface IPriceDetails {
    original_price: number;
    original_price_formatted: string;
    price: number;
    price_formatted: string;
    price_per_unit: number;
    price_per_unit_formatted: string;
}

export interface IPrice {
    currency: string;
    is_discounted: boolean;
    include_tax: IPriceDetails;
    exclude_tax?: IPriceDetails;
}

export interface IPriceData {
    currency: string;
    price: number;
    priceFormatted: string;
    originalPrice: number;
    originalPriceFormatted: string;
    pricePerUnit: number;
    pricePerUnitFormatted: string;
    isDiscounted: boolean;
}

export interface IPriceRangeData {
    formattedMinPrice: string;
    formattedMaxPrice: string;
    maxDiscountPercent?: number;
}

export class Price {
    @observable public currency: string = '';
    @observable public price: number;
    @observable public priceFormatted: string = '';
    @observable public originalPrice: number;
    @observable public originalPriceFormatted: string = '';
    @observable public pricePerUnit: number;
    @observable public pricePerUnitFormatted: string = '';
    @observable public isDiscounted: boolean;

    public static fromPlainObject(price: Price): Price {
        const newPrice = new Price();

        copyPropertiesExcludeNullsAndGetters(newPrice, price);

        return newPrice;
    }

    public static fromJson(price: IPrice): Price {
        const newPrice = new Price();
        if (!price) {
            return newPrice;
        }

        const priceDetails = price.exclude_tax || price.include_tax;

        const priceDataMapping = {
            currency: price.currency,
            originalPriceFormatted: priceDetails.original_price_formatted,
            priceFormatted: priceDetails.price_formatted,
            pricePerUnit: priceDetails.price_per_unit,
            pricePerUnitFormatted: priceDetails.price_per_unit_formatted
        };
        copyPropertiesExcludeNullsAndGetters(newPrice, priceDataMapping);

        newPrice.isDiscounted = price.is_discounted;
        newPrice.originalPrice = priceDetails.original_price;
        newPrice.price = priceDetails.price;

        return newPrice;
    }

    public toJSON(): IPriceData {
        const {
            currency,
            price,
            priceFormatted,
            originalPrice,
            originalPriceFormatted,
            pricePerUnit,
            pricePerUnitFormatted,
            isDiscounted
        } = this;

        return {
            currency,
            price,
            priceFormatted,
            originalPrice,
            originalPriceFormatted,
            pricePerUnit,
            pricePerUnitFormatted,
            isDiscounted
        };
    }
}
